import i18n from '@/utils/i18n';
import { ROUTE } from '@/routes/constants';
import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

const onResponse = (response: AxiosResponse): AxiosResponse => {
    const data = response.data;
    if ((data?.code === 80007000)) {
        console.log('[onResponse] caught restricted response:', data);
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = ROUTE.ACCESS_RESTRICTED;
    }
    return response;
}

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
    console.error('[response error]', error.message, error.response?.data);
    const status = error.response?.status;
    const code = (error.response?.data as any)?.code;

    // 访问时间受限（运营时段门控）：优先判定，避免被下方状态码分支遮蔽。
    if (code === 80007000) {
        console.log('[access restricted] code 80007000，清理本地状态并跳转受限页');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/auth/access-restricted';
        return Promise.reject(error);
    }

    // 仅 401 视为「会话失效」→ 全局登出并跳登录页（后端对黑名单/失效 token 返回 401）。
    //
    // ⚠️ 403 不再全局登出：后端对「资源无权」和「过期/错误 token」都返回**裸 403**
    // （空 body、无 code），前端在拦截器层无法区分二者。若一律登出，会把**有效会话**
    // 一并清掉——典型：getSubjectList 对未挂科目的账号恒 403，登录后进学生首页即被踢回
    // 登录页（测试环境高发，多个接口还会间歇 403）。资源级 403 交由各调用点本地兜底
    // （如 useSubjectList 的 SWR error + 空态）；真正的 token 失效仍由 ProtectedRoute +
    // tokenLogin 在加载/换页时兜住并跳登录页，不依赖此拦截器。
    if (status === 401) {
        console.log('[401 response] token 已失效或被登出，清理本地状态并跳转登录页');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = ROUTE.LOGIN;
        return Promise.reject(error);
    }

    return Promise.reject(error);
}

export const AxiosInstance = Axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  paramsSerializer: (params) => {
    return Object.entries(params)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}=${value.join(',')}`;
        }
        return `${key}=${value}`;
      })
      .join('&');
  },
});

AxiosInstance.interceptors.response.use(onResponse, onResponseError);
AxiosInstance.interceptors.request.use(
  (config) => {
    // 从 i18n 获取当前语言（如 'zh-CN'、'en-US'）
    const currentLanguage = i18n.language || 'zh-HK';

    // 设置 Accept-Language 请求头
    if (currentLanguage) {
      config.headers['Accept-Language'] = currentLanguage;
    }

    // 调试：检查总 header 大小
    if (import.meta.env.DEV) {
      const headerSize = JSON.stringify(config.headers).length;
      if (headerSize > 4096) {
        console.error('[AxiosInstance] 请求头过大:', headerSize, 'bytes, URL:', config.url);
        console.error('[AxiosInstance] Headers:', config.headers);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 认证入口接口不需要带旧 token
const PUBLIC_ENDPOINTS = [
    '/api/user/getSalt',
    '/api/user/login',
    '/api/user/register/teacher',
    '/api/user/register/student',
];

export const customInstance = <T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
  ): Promise<T> => {
    const source = Axios.CancelToken.source();
    const token = localStorage.getItem('token');
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(path => config.url?.includes(path));

    if (token && !isPublicEndpoint) {
        // 调试：检查 token 长度是否异常
        if (token.length > 1000) {
          console.warn('[AxiosInstance] Token 长度异常:', token.length, 'URL:', config.url);
          console.warn('[AxiosInstance] Token 前100字符:', token.substring(0, 100));
        }
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
        };
    }
    config.headers = {
      ...config.headers,
      ...options?.headers,
    };
    
    const promise = AxiosInstance({
      ...config,
      cancelToken: source.token,
    }).then(({ data }) => data);
  
    // @ts-ignore
    promise.cancel = () => {
      source.cancel('Query was cancelled');
    };
  
    return promise;
  };

// TODO: request through network
export const BASE_QUESTION_IMAGE_URL = import.meta.env.VITE_API_BASE + '/images/question/';
export const BASE_ANSWER_IMAGE_URL = import.meta.env.VITE_API_BASE + '/images/answer/';